import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { Card } from 'reactstrap';

import CallexLoader from '../../../components/CallexLoader';
import { checkModulePermission } from '../../../utils/common';
import AdminHeader from '../../../components/header/AdminHeader';
import Pagination from '../../../components/form/Pagination';
import AddEditAllowIpModal from './AddEditAllowIpModal';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  allowIpDelete,
  allowIpListGet,
  useAllowIpList
} from '../../../store/slices/tenantSlices/settingSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';

const PERMISSION_KEY = 'Allow IP';

const AllowIp = () => {
  const dispatch = useDispatch();
  const allowIpList = useAllowIpList();
  const userRolePermission = useUserRolePermission();

  const [isLoading, setIsLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchAllowIpList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      await dispatch(allowIpListGet({ ...filter })).unwrap();
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  useEffect(() => {
    fetchAllowIpList();
  }, [filter]);

  const deleteAllowIp = async () => {
    setIsDeleting(true);
    try {
      const response = await dispatch(allowIpDelete(isDeleteId)).unwrap();
      if (response && response.success) {
        toast.success(response.message || 'Record deleted successfully');
        if (allowIpList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchAllowIpList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      <AdminHeader
        title="Allow IP"
        isExpandSearch
        searchPlaceholder="Search Allow IP"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, page: 1, search: e.target.value })}
        isAddDisabled={!checkModulePermission('is_add', PERMISSION_KEY, userRolePermission)}
        isAdd
        addBtnName="Add Allow IP"
        onAddClick={() => setOpenAddModal(true)}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : allowIpList?.data?.length > 0 &&
        checkModulePermission('is_view', PERMISSION_KEY, userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>IP Address</th>
                  <th>Description</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {allowIpList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.ip || '-'}</td>
                      <td>{val?.description || '-'}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', PERMISSION_KEY, userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_edit', PERMISSION_KEY, userRolePermission)
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission('is_delete', PERMISSION_KEY, userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_delete',
                                    PERMISSION_KEY,
                                    userRolePermission
                                  )
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={allowIpList?.total}
              limit={allowIpList?.limit}
              page={allowIpList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchAllowIpList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteAllowIp()}
        isLoading={isDeleting}
      />
      <AddEditAllowIpModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchAllowIpList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
    </div>
  );
};

export default AllowIp;
